import React, { useState, useEffect } from 'react';

const BalanceReductionForm = ({ record, onSave, onClose, currentUser }) => {
  const [formData, setFormData] = useState({
    mes: '',
    seguimiento: '',
    fecha_asunto: '',
    nombre_cliente: '',
    cuenta_publicitaria: '',
    celula: '',
    campana: '',
    asunto: '',
    acciones: '',
    status: 'pendiente',
    fechaBajaSaldo: '',
    fechaSubidaSaldo: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);

  // Opciones predefinidas
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const celulas = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4'
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente' },
    { value: 'en_proceso', label: '🔄 En Proceso' },
    { value: 'completado', label: '✅ Completado' },
    { value: 'cancelado', label: '❌ Cancelado' }
  ];

  useEffect(() => {
    loadUsers();
    loadClients();
    if (record) {
      setFormData({
        mes: record.mes || '',
        seguimiento: record.seguimiento || '',
        fecha_asunto: record.fecha_asunto ? record.fecha_asunto.split('T')[0] : '',
        nombre_cliente: record.nombre_cliente || '',
        cuenta_publicitaria: record.cuenta_publicitaria || '',
        celula: record.celula || '',
        campana: record.campana || '',
        asunto: record.asunto || '',
        acciones: record.acciones || '',
        status: record.status || 'pendiente',
        fechaBajaSaldo: record.fechaBajaSaldo ? record.fechaBajaSaldo.split('T')[0] : '',
        fechaSubidaSaldo: record.fechaSubidaSaldo ? record.fechaSubidaSaldo.split('T')[0] : ''
      });
    } else {
      // Para nuevos registros, establecer el mes actual y el usuario actual como seguimiento
      const currentMonth = new Date().toLocaleDateString('es-ES', { month: 'long' });
      const capitalizedMonth = currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1);
      setFormData(prev => ({
        ...prev,
        mes: capitalizedMonth,
        fecha_asunto: new Date().toISOString().split('T')[0],
        seguimiento: currentUser?.name || ''
      }));
    }
  }, [record, currentUser]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/users`);
      if (response.ok) {
        const data = await response.json();
        console.log('Usuarios cargados desde API:', data);
        setUsers(data);
      } else {
        console.error('Error al cargar usuarios:', response.status);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/clients`);
      if (response.ok) {
        const data = await response.json();
        console.log('Clientes cargados desde API:', data);
        setClients(data);
      } else {
        console.error('Error al cargar clientes:', response.status);
        setClients([]);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleClientNameChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      nombre_cliente: value
    }));

    // Filtrar clientes basado en el texto ingresado
    if (value.length > 0) {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowClientSuggestions(true);
    } else {
      setFilteredClients([]);
      setShowClientSuggestions(false);
    }

    // Limpiar error del campo
    if (errors.nombre_cliente) {
      setErrors(prev => ({
        ...prev,
        nombre_cliente: ''
      }));
    }
  };

  const selectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      nombre_cliente: client.name
    }));
    setShowClientSuggestions(false);
    setFilteredClients([]);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.mes.trim()) newErrors.mes = 'El mes es requerido';
    if (!formData.fecha_asunto) newErrors.fecha_asunto = 'La fecha de asunto es requerida';
    if (!formData.nombre_cliente.trim()) newErrors.nombre_cliente = 'El nombre del cliente es requerido';
    if (!formData.cuenta_publicitaria.trim()) newErrors.cuenta_publicitaria = 'La cuenta publicitaria es requerida';
    if (!formData.celula.trim()) newErrors.celula = 'La célula es requerida';
    if (!formData.campana.trim()) newErrors.campana = 'La campaña es requerida';
    if (!formData.asunto.trim()) newErrors.asunto = 'El asunto es requerido';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error al guardar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">
                {record ? '✏️ Editar Registro' : '➕ Nuevo Registro'}
              </h2>
              <p className="text-red-100 mt-1">
                Cliente que baja saldo publicitario
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Primera fila */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mes *
                </label>
                <select
                  name="mes"
                  value={formData.mes}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                    errors.mes ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Seleccionar mes</option>
                  {meses.map(mes => (
                    <option key={mes} value={mes}>{mes}</option>
                  ))}
                </select>
                {errors.mes && <p className="text-red-500 text-sm mt-1">{errors.mes}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Agente de Seguimiento
                </label>
                {loadingUsers ? (
                  <div className="w-full p-3 border-2 border-gray-300 rounded-xl bg-gray-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500 mr-2"></div>
                    <span className="text-gray-500 text-sm">Cargando agentes...</span>
                  </div>
                ) : (
                  <select
                    name="seguimiento"
                    value={formData.seguimiento}
                    onChange={handleChange}
                    className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Seleccionar agente</option>
                    {users.map(user => {
                      const fullName = user.firstName && user.lastName 
                        ? `${user.firstName} ${user.lastName}` 
                        : (user.firstName || user.lastName || user.email);
                      
                      return (
                        <option key={user.email || user.id} value={fullName}>
                          {fullName} ({user.role || 'usuario'})
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Asunto *
                </label>
                <input
                  type="date"
                  name="fecha_asunto"
                  value={formData.fecha_asunto}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                    errors.fecha_asunto ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.fecha_asunto && <p className="text-red-500 text-sm mt-1">{errors.fecha_asunto}</p>}
              </div>
            </div>

            {/* Segunda fila */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre del Cliente *
                </label>
                {loadingClients ? (
                  <div className="w-full p-3 border-2 border-gray-300 rounded-xl bg-gray-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500 mr-2"></div>
                    <span className="text-gray-500 text-sm">Cargando clientes...</span>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      name="nombre_cliente"
                      value={formData.nombre_cliente}
                      onChange={handleClientNameChange}
                      onFocus={() => {
                        if (formData.nombre_cliente.length > 0) {
                          const filtered = clients.filter(client =>
                            client.name.toLowerCase().includes(formData.nombre_cliente.toLowerCase())
                          );
                          setFilteredClients(filtered);
                          setShowClientSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay para permitir click en sugerencias
                        setTimeout(() => setShowClientSuggestions(false), 200);
                      }}
                      placeholder="Escribe el nombre del cliente..."
                      className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                        errors.nombre_cliente ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    
                    {/* Sugerencias de clientes */}
                    {showClientSuggestions && filteredClients.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredClients.slice(0, 10).map((client, index) => (
                          <div
                            key={client.id || index}
                            onClick={() => selectClient(client)}
                            className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{client.name}</div>
                            {client.specialty && (
                              <div className="text-sm text-gray-500">{client.specialty}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {errors.nombre_cliente && <p className="text-red-500 text-sm mt-1">{errors.nombre_cliente}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Link Cuenta Publicitaria *
                </label>
                <input
                  type="url"
                  name="cuenta_publicitaria"
                  value={formData.cuenta_publicitaria}
                  onChange={handleChange}
                  placeholder="https://business.facebook.com/... o https://ads.google.com/..."
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                    errors.cuenta_publicitaria ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.cuenta_publicitaria && <p className="text-red-500 text-sm mt-1">{errors.cuenta_publicitaria}</p>}
              </div>
            </div>

            {/* Tercera fila */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Célula *
                </label>
                <select
                  name="celula"
                  value={formData.celula}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                    errors.celula ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Seleccionar célula</option>
                  {celulas.map(celula => (
                    <option key={celula} value={celula}>{celula}</option>
                  ))}
                </select>
                {errors.celula && <p className="text-red-500 text-sm mt-1">{errors.celula}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Campaña */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Campaña *
              </label>
              <input
                type="text"
                name="campana"
                value={formData.campana}
                onChange={handleChange}
                placeholder="Nombre de la campaña"
                className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                  errors.campana ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.campana && <p className="text-red-500 text-sm mt-1">{errors.campana}</p>}
            </div>

            {/* Fecha para bajar saldo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fecha para bajar saldo
              </label>
              <input
                type="date"
                name="fechaBajaSaldo"
                value={formData.fechaBajaSaldo || ''}
                onChange={handleChange}
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Fecha para subir saldo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fecha para subir saldo
              </label>
              <input
                type="date"
                name="fechaSubidaSaldo"
                value={formData.fechaSubidaSaldo || ''}
                onChange={handleChange}
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Asunto */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Asunto *
              </label>
              <textarea
                name="asunto"
                value={formData.asunto}
                onChange={handleChange}
                placeholder="Describe el asunto relacionado con la reducción de saldo..."
                rows="4"
                className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                  errors.asunto ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.asunto && <p className="text-red-500 text-sm mt-1">{errors.asunto}</p>}
            </div>

            {/* Acciones */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Acciones
              </label>
              <textarea
                name="acciones"
                value={formData.acciones}
                onChange={handleChange}
                placeholder="Describe las acciones tomadas o planificadas..."
                rows="3"
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg hover:from-red-700 hover:to-orange-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Guardando...
              </div>
            ) : (
              record ? 'Actualizar Registro' : 'Crear Registro'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BalanceReductionForm;
